export const CANDIDATE_FINDER_SCORING_VERSION = 'backlist_candidate_v1'

export type RightsStatus = 'controlled' | 'uncertain' | 'unavailable'
export type BacklistPerformance = 'strong' | 'middle' | 'occasional' | 'rare' | 'unknown'
export type ReaderProof = 'strong' | 'some' | 'limited' | 'concerns'
export type BookPosition =
  | 'series_opener'
  | 'standalone'
  | 'later_prior_available'
  | 'later_prior_unavailable'
export type SeriesPipeline = 'completed' | 'next_ready' | 'planned' | 'none' | 'standalone'
export type ReadThrough = 'strong' | 'moderate' | 'weak_unknown' | 'standalone'
export type PackageReadiness = 'current' | 'one_needs_work' | 'both_need_work'
export type PositioningClarity = 'clear' | 'mostly_clear' | 'difficult'
export type MarketingCapacity = 'specific_plan' | 'some_channels' | 'no_plan'
export type FinancialFeasibility = 'can_support' | 'needs_budget' | 'not_feasible'

export interface CandidateAnswers {
  title: string
  rights?: RightsStatus
  backlistPerformance?: BacklistPerformance
  readerProof?: ReaderProof
  bookPosition?: BookPosition
  seriesPipeline?: SeriesPipeline
  readThrough?: ReadThrough
  packageReadiness?: PackageReadiness
  positioningClarity?: PositioningClarity
  marketingCapacity?: MarketingCapacity
  financialFeasibility?: FinancialFeasibility
}

export type CandidateTier = 'strong' | 'promising' | 'not_ready' | 'rights_review' | 'ineligible'

export interface CandidateDimensionScores {
  evidence: number
  leverage: number
  readiness: number
}

export interface ScoredCandidate {
  index: number
  title: string
  score: number | null
  tier: CandidateTier
  dimensions: CandidateDimensionScores | null
  positiveFactors: string[]
  preparationTasks: string[]
  warnings: string[]
  limitedEvidence: boolean
  answers: CandidateAnswers
}

const PERFORMANCE_POINTS: Record<BacklistPerformance, number> = {
  strong: 20,
  middle: 14,
  occasional: 8,
  rare: 3,
  unknown: 7,
}

const READER_POINTS: Record<ReaderProof, number> = {
  strong: 10,
  some: 7,
  limited: 3,
  concerns: 0,
}

const POSITION_POINTS: Record<BookPosition, number> = {
  series_opener: 15,
  standalone: 11,
  later_prior_available: 10,
  later_prior_unavailable: 0,
}

const PIPELINE_POINTS: Record<SeriesPipeline, number> = {
  completed: 15,
  next_ready: 11,
  planned: 6,
  none: 0,
  standalone: 10,
}

const READ_THROUGH_POINTS: Record<ReadThrough, number> = {
  strong: 10,
  moderate: 6,
  weak_unknown: 2,
  standalone: 6,
}

const PACKAGE_POINTS: Record<PackageReadiness, number> = {
  current: 10,
  one_needs_work: 5,
  both_need_work: 0,
}

const POSITIONING_POINTS: Record<PositioningClarity, number> = {
  clear: 8,
  mostly_clear: 4,
  difficult: 1,
}

const MARKETING_POINTS: Record<MarketingCapacity, number> = {
  specific_plan: 7,
  some_channels: 4,
  no_plan: 1,
}

const FINANCIAL_POINTS: Record<FinancialFeasibility, number> = {
  can_support: 5,
  needs_budget: 3,
  not_feasible: 0,
}

const pointsFor = <T extends string>(mapping: Record<T, number>, value: T | undefined): number => {
  if (!value) return 0
  const points = mapping[value]
  return typeof points === 'number' ? points : 0
}

export const displayCandidateTitle = (candidate: CandidateAnswers, index: number): string => {
  const title = candidate.title.trim()
  return title || `Book ${index + 1}`
}

const buildPositiveFactors = (candidate: CandidateAnswers): Array<{ points: number; order: number; copy: string }> => {
  const factors: Array<{ points: number; order: number; copy: string }> = []
  const add = (points: number, order: number, copy?: string) => {
    if (copy) factors.push({ points, order, copy })
  }

  add(
    pointsFor(PERFORMANCE_POINTS, candidate.backlistPerformance),
    1,
    candidate.backlistPerformance === 'strong'
      ? 'It is one of your strongest consistent sellers.'
      : candidate.backlistPerformance === 'middle'
        ? 'It has a dependable sales record within your backlist.'
        : candidate.backlistPerformance === 'occasional'
          ? 'It already has some sales evidence to build from.'
          : undefined,
  )
  add(
    pointsFor(READER_POINTS, candidate.readerProof),
    2,
    candidate.readerProof === 'strong'
      ? 'Reviews and reader response provide strong proof of appeal.'
      : candidate.readerProof === 'some'
        ? 'It has positive reader proof.'
        : undefined,
  )
  add(
    pointsFor(POSITION_POINTS, candidate.bookPosition),
    3,
    candidate.bookPosition === 'series_opener'
      ? 'It gives translated readers the right entry point to a series.'
      : candidate.bookPosition === 'standalone'
        ? 'It works as a self-contained translation project.'
        : candidate.bookPosition === 'later_prior_available'
          ? 'Earlier books are already available for readers in the target language.'
          : undefined,
  )
  add(
    pointsFor(PIPELINE_POINTS, candidate.seriesPipeline),
    4,
    candidate.seriesPipeline === 'completed'
      ? 'Several follow-up books are ready to support continued reading.'
      : candidate.seriesPipeline === 'next_ready'
        ? 'The next book is ready if translated readers continue.'
        : candidate.seriesPipeline === 'standalone'
          ? 'A standalone release does not depend on a continuation schedule.'
          : undefined,
  )
  add(
    pointsFor(READ_THROUGH_POINTS, candidate.readThrough),
    5,
    candidate.readThrough === 'strong'
      ? 'Existing read-through is strong.'
      : candidate.readThrough === 'moderate'
        ? 'Existing read-through provides useful demand evidence.'
        : undefined,
  )
  add(
    pointsFor(PACKAGE_POINTS, candidate.packageReadiness),
    6,
    candidate.packageReadiness === 'current'
      ? 'Its cover and metadata are already current.'
      : undefined,
  )
  add(
    pointsFor(POSITIONING_POINTS, candidate.positioningClarity),
    7,
    candidate.positioningClarity === 'clear'
      ? 'Its genre, audience, and hook are easy to explain.'
      : undefined,
  )
  add(
    pointsFor(MARKETING_POINTS, candidate.marketingCapacity),
    8,
    candidate.marketingCapacity === 'specific_plan'
      ? 'You have a defined audience and a realistic promotion plan.'
      : undefined,
  )
  add(
    pointsFor(FINANCIAL_POINTS, candidate.financialFeasibility),
    9,
    candidate.financialFeasibility === 'can_support'
      ? 'You can support both translation and launch activity.'
      : undefined,
  )

  return factors
}

const buildPreparationTasks = (candidate: CandidateAnswers): string[] => {
  const tasks: string[] = []
  const add = (copy?: string) => {
    if (copy && !tasks.includes(copy)) tasks.push(copy)
  }

  if (candidate.readerProof === 'concerns') {
    add('Review recurring reader concerns and decide what should be corrected before translation.')
  }
  if (candidate.bookPosition === 'later_prior_unavailable') {
    add('Start with the series entry point, or make the preceding books available in the chosen language first.')
  }
  if (candidate.seriesPipeline === 'none' && candidate.bookPosition === 'series_opener') {
    add('Define a realistic continuation plan before launching a translated series opener.')
  }
  if (candidate.packageReadiness === 'both_need_work') {
    add('Refresh both the cover and metadata before researching a launch.')
  } else if (candidate.packageReadiness === 'one_needs_work') {
    add('Refresh the cover or metadata item that is no longer current.')
  }
  if (candidate.positioningClarity === 'difficult') {
    add('Clarify the genre, audience, and central hook in one short positioning statement.')
  } else if (candidate.positioningClarity === 'mostly_clear') {
    add('Tighten the positioning so the genre and promise remain clear outside the current market.')
  }
  if (candidate.marketingCapacity === 'no_plan') {
    add('Identify a reachable audience and a modest launch plan for one target market.')
  } else if (candidate.marketingCapacity === 'some_channels') {
    add('Turn the channels you already have into a specific launch plan.')
  }
  if (candidate.financialFeasibility === 'not_feasible') {
    add('Build a feasible translation, review, packaging, and launch budget before proceeding.')
  } else if (candidate.financialFeasibility === 'needs_budget') {
    add('Set a budget for translation, review, packaging, and launch support.')
  }
  if (candidate.backlistPerformance === 'unknown') {
    add('Gather more sales and reader evidence before treating this as a proven backlist candidate.')
  } else if (candidate.backlistPerformance === 'rare') {
    add('Confirm that another title does not offer stronger existing reader demand.')
  }

  return tasks.slice(0, 3)
}

export const scoreCandidate = (candidate: CandidateAnswers, index: number): ScoredCandidate => {
  const title = displayCandidateTitle(candidate, index)

  if (candidate.rights === 'uncertain') {
    return {
      index,
      title,
      score: null,
      tier: 'rights_review',
      dimensions: null,
      positiveFactors: [],
      preparationTasks: [
        'Review your publishing agreement or speak with your agent, publisher, or qualified adviser before proceeding.',
      ],
      warnings: ['Translation and territorial rights have not been confirmed.'],
      limitedEvidence: false,
      answers: candidate,
    }
  }

  if (candidate.rights !== 'controlled') {
    return {
      index,
      title,
      score: null,
      tier: 'ineligible',
      dimensions: null,
      positiveFactors: [],
      preparationTasks: ['Choose a title for which you control the necessary translation and publishing rights.'],
      warnings: ['This title should not proceed while the necessary rights are unavailable.'],
      limitedEvidence: false,
      answers: candidate,
    }
  }

  const evidence =
    pointsFor(PERFORMANCE_POINTS, candidate.backlistPerformance) +
    pointsFor(READER_POINTS, candidate.readerProof) +
    pointsFor(READ_THROUGH_POINTS, candidate.readThrough)
  const leverage =
    pointsFor(POSITION_POINTS, candidate.bookPosition) +
    pointsFor(PIPELINE_POINTS, candidate.seriesPipeline)
  const readiness =
    pointsFor(PACKAGE_POINTS, candidate.packageReadiness) +
    pointsFor(POSITIONING_POINTS, candidate.positioningClarity) +
    pointsFor(MARKETING_POINTS, candidate.marketingCapacity) +
    pointsFor(FINANCIAL_POINTS, candidate.financialFeasibility)

  let score = evidence + leverage + readiness
  if (candidate.seriesPipeline === 'none' && candidate.bookPosition === 'series_opener') {
    score -= 8
  }
  if (candidate.bookPosition === 'later_prior_unavailable') {
    score = Math.min(score, 54)
  }
  score = Math.max(0, Math.min(100, score))

  const limitedEvidence =
    candidate.backlistPerformance === 'unknown' || candidate.readThrough === 'weak_unknown'
  const hardPreparationNeeded =
    candidate.financialFeasibility === 'not_feasible' ||
    (candidate.bookPosition === 'series_opener' && candidate.seriesPipeline === 'none') ||
    limitedEvidence

  let tier: CandidateTier = 'not_ready'
  if (score >= 75 && !hardPreparationNeeded) {
    tier = 'strong'
  } else if (score >= 55) {
    tier = 'promising'
  }

  const warnings: string[] = []
  if (candidate.readerProof === 'concerns') {
    warnings.push('Readers have raised significant recurring concerns that should be addressed before translation.')
  }
  if (candidate.bookPosition === 'later_prior_unavailable') {
    warnings.push('Readers would be missing the series entry point in the language being considered.')
  }
  if (candidate.financialFeasibility === 'not_feasible') {
    warnings.push('The translation and launch are not currently financially feasible.')
  }
  if (limitedEvidence) {
    warnings.push('The recommendation has limited performance or read-through evidence.')
  }

  return {
    index,
    title,
    score,
    tier,
    dimensions: { evidence, leverage, readiness },
    positiveFactors: buildPositiveFactors(candidate)
      .sort((left, right) => right.points - left.points || left.order - right.order)
      .slice(0, 3)
      .map(factor => factor.copy),
    preparationTasks: buildPreparationTasks(candidate),
    warnings,
    limitedEvidence,
    answers: candidate,
  }
}

const isSeriesOpener = (result: ScoredCandidate): boolean =>
  result.answers.bookPosition === 'series_opener'

export const rankCandidates = (candidates: CandidateAnswers[]): ScoredCandidate[] =>
  candidates
    .map(scoreCandidate)
    .sort((left, right) => {
      const leftEligible = left.score !== null
      const rightEligible = right.score !== null
      if (leftEligible !== rightEligible) return leftEligible ? -1 : 1
      if (!leftEligible || !rightEligible) return left.index - right.index

      if (right.score !== left.score) return (right.score || 0) - (left.score || 0)
      if (isSeriesOpener(left) !== isSeriesOpener(right)) return isSeriesOpener(left) ? -1 : 1

      const performanceDifference =
        pointsFor(PERFORMANCE_POINTS, right.answers.backlistPerformance) -
        pointsFor(PERFORMANCE_POINTS, left.answers.backlistPerformance)
      if (performanceDifference) return performanceDifference

      const pipelineDifference =
        pointsFor(PIPELINE_POINTS, right.answers.seriesPipeline) -
        pointsFor(PIPELINE_POINTS, left.answers.seriesPipeline)
      if (pipelineDifference) return pipelineDifference

      const packageDifference =
        pointsFor(PACKAGE_POINTS, right.answers.packageReadiness) -
        pointsFor(PACKAGE_POINTS, left.answers.packageReadiness)
      if (packageDifference) return packageDifference

      return left.index - right.index
    })

export const tierLabel = (tier: CandidateTier): string => {
  switch (tier) {
    case 'strong':
      return 'Strong first candidate'
    case 'promising':
      return 'Promising, with preparation'
    case 'not_ready':
      return 'Needs substantial preparation'
    case 'rights_review':
      return 'Rights review needed'
    case 'ineligible':
      return 'Not currently eligible'
  }
}

export const dimensionLabel = (score: number, maximum: number): string => {
  const ratio = maximum > 0 ? score / maximum : 0
  if (ratio >= 0.75) return 'Strong'
  if (ratio >= 0.5) return 'Developing'
  return 'Needs work'
}

export const comparisonReason = (candidate: ScoredCandidate, leader: ScoredCandidate): string => {
  if (candidate.tier === 'rights_review') return 'Translation rights still need to be confirmed.'
  if (candidate.tier === 'ineligible') return 'The necessary translation rights are not currently available.'
  if (candidate.answers.bookPosition === 'later_prior_unavailable') {
    return 'It is a later series book without the translated entry point available first.'
  }
  if (candidate.preparationTasks.length > 0) return candidate.preparationTasks[0]
  if (candidate.score === leader.score) return 'The published tie-break rules favored the recommended title.'
  return 'It has less combined evidence, portfolio leverage, or launch readiness.'
}
